import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MIN_PASSWORD_LENGTH, passwordHint, passwordLengthProblem } from "@talentkontor/domain";
import { api } from "../../data";
import { useSession } from "../../app/session";
import { InternRahmen } from "../intern/InternRahmen";
import { describeError } from "../../components/States";
import { authFehler, authFehlerArt } from "../konto/auth";
import { CaptchaFeld, captchaConfig } from "../konto/Captcha";
import { PostfachPruefen } from "../konto/PostfachPruefen";
import { date } from "../../lib/format";
import { acceptStaffInvitation, ROLLE, staffInvitationInfo, rollenErklaerung } from "./team-data";

/**
 * Einladung ins Team der Betreiber-Konsole annehmen (Vertrieb-Konsole, Inhaber 02.11.2026). Der Mail-Link lautet seit dem
 * internen Bereich (03.11.2026, 0496) /intern/einladung?t=<Schlüssel> (alte /betrieb/einladung-Links leiten um), arbeitet mit
 * der internen Sitzung und liegt bewusst außerhalb des Zugangstors der Konsole: angenommen wird mit der
 * eingeladenen, bestätigten Adresse; danach verlangt die Konsole den zweiten Faktor (BetriebFaktorPflicht).
 * Wer noch kein Konto hat, legt es hier mit der eingeladenen Adresse an und kommt über den Code aus der Mail zurück.
 */
export function TeamEinladung() {
  const [params] = useSearchParams();
  const token = (params.get("t") ?? "").trim();
  const { session, ready } = useSession();
  const qc = useQueryClient();
  const weiter = `/intern/einladung?t=${encodeURIComponent(token)}`;
  const info = useQuery({ queryKey: ["platform_staff_invitation_info", token, session?.userId ?? null], enabled: !!session && !!token,
    queryFn: () => staffInvitationInfo(api, token), retry: false });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);

  async function annehmen() {
    setBusy(true);
    setFehler(null);
    try {
      const r = await acceptStaffInvitation(api, token);
      setFertig(ROLLE[r.role]);
      await qc.invalidateQueries({ queryKey: ["account"] });
    } catch (e) {
      const d = describeError(e);
      setFehler(`${d.title} ${d.text}`.trim());
    } finally {
      setBusy(false);
    }
  }

  let inhalt;
  if (!token) {
    inhalt = <Hinweis titel="Dieser Einladungslink ist unvollständig." text="Öffne den Link aus der Einladungs-Mail vollständig oder bitte einen Admin um eine neue Einladung." />;
  } else if (!ready) {
    inhalt = <p className="muted">Wird geladen …</p>;
  } else if (!session) {
    inhalt = <Anmelden weiter={weiter} />;
  } else if (fertig) {
    inhalt = (
      <div className="panel stack" role="status">
        <h2>Willkommen im Team.</h2>
        <p>Du bist jetzt in der Betreiber-Konsole als {fertig} eingetragen. Beim ersten Öffnen richtest du den zweiten Faktor ein (Authenticator-App); danach fragt die Konsole bei jeder Anmeldung nach dem Code.</p>
        <div className="row"><a className="btn primary" href="/intern">Zur Konsole</a></div>
      </div>
    );
  } else if (info.isPending) {
    inhalt = <p className="muted">Einladung wird geprüft …</p>;
  } else if (info.error || !info.data || info.data.status === "unknown") {
    inhalt = <Hinweis titel="Diesen Einladungslink gibt es nicht." text="Bitte einen Admin um eine neue Einladung." />;
  } else if (info.data.status !== "open") {
    inhalt = info.data.status === "accepted"
      ? <Hinweis titel="Diese Einladung wurde schon angenommen." text="Melde dich mit dem eingeladenen Konto im internen Bereich an." link={{ to: "/intern", label: "Zur Konsole" }} />
      : <Hinweis titel={info.data.status === "expired" ? "Diese Einladung ist abgelaufen." : "Diese Einladung wurde zurückgenommen."} text="Bitte einen Admin um eine neue Einladung." />;
  } else if (info.data.email_matches === false) {
    inhalt = (
      <div className="panel stack" role="alert">
        <h2>Diese Einladung gilt für eine andere E-Mail-Adresse.</h2>
        <p>Du bist angemeldet als {session.email}. Annehmen kann sie nur das eingeladene Konto. Melde dich ab und mit der Adresse aus der Einladungs-Mail an.</p>
        <div className="row"><button type="button" className="primary" onClick={() => void api.signOut()}>Abmelden</button></div>
      </div>
    );
  } else {
    inhalt = (
      <div className="panel stack">
        <h2>Einladung als {ROLLE[info.data.role ?? "sales"]}</h2>
        <p>Angemeldet als {session.email}. Die Einladung gilt bis zum {date(info.data.expires_at ?? null)}.</p>
        <p className="muted">
          {rollenErklaerung(info.data.role ?? "sales")}
          {" "}Für die Konsole brauchst du einen zweiten Faktor. Alle Aktionen werden protokolliert.
        </p>
        <div className="row">
          <button type="button" className="primary" disabled={busy} aria-busy={busy} onClick={() => void annehmen()}>{busy ? "Wird angenommen …" : "Einladung annehmen"}</button>
        </div>
        {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      </div>
    );
  }
  return (
    <InternRahmen title="Einladung ins Team">
      <div className="stack tk-einladung co-intern-einladung">
        <div className="pagehead"><div><div className="eyebrow">Interner Bereich</div><h1>Einladung ins Team</h1></div></div>
        {inhalt}
      </div>
    </InternRahmen>
  );
}

function Hinweis({ titel, text, link }: { titel: string; text: string; link?: { to: string; label: string } }) {
  return (
    <div className="panel stack" role="alert">
      <h2>{titel}</h2>
      <p>{text}</p>
      {link && <div className="row"><Link className="btn" to={link.to}>{link.label}</Link></div>}
    </div>
  );
}

/** Nicht angemeldet: mit vorhandenem Konto anmelden oder ein neues mit der eingeladenen Adresse anlegen */
function Anmelden({ weiter }: { weiter: string }) {
  const [neu, setNeu] = useState(false);
  const [email, setEmail] = useState("");
  const [passwort, setPasswort] = useState("");
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bestaetigen, setBestaetigen] = useState<string | null>(null);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();
  const anmelden = `/intern/anmelden?weiter=${encodeURIComponent(weiter)}`;

  async function anlegen(e: FormEvent) {
    e.preventDefault();
    const mail = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail)) { setFehler("Bitte die eingeladene E-Mail-Adresse eingeben."); return; }
    const zuKurz = api.mode !== "demo" ? passwordLengthProblem(passwort, "de") : null;
    if (zuKurz) { setFehler(zuKurz); return; }
    if (mitCaptcha && !captcha) { setFehler("Bitte die Sicherheitsprüfung lösen."); return; }
    setBusy(true);
    setFehler(null);
    try {
      const r = await api.signUp(mail, passwort, { captchaToken: captcha, redirectTo: weiter, language: "de" });
      if (r.needsConfirmation) setBestaetigen(mail);
      else window.location.assign(weiter);
    } catch (err) {
      setFehler(authFehlerArt(err) === "user_exists" ? "Zu dieser Adresse gibt es schon ein Konto. Bitte melde dich damit an." : authFehler(err));
      setRunde((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  if (bestaetigen) {
    return <PostfachPruefen email={bestaetigen} redirectTo={weiter} ziel="einladung" onBack={() => { setBestaetigen(null); setPasswort(""); }} onVerified={() => window.location.assign(weiter)} />;
  }
  return (
    <div className="panel stack">
      <h2>Mit der eingeladenen Adresse anmelden</h2>
      <p>Die Einladung gilt nur für die Adresse, an die die Mail ging. Hast du damit schon ein Konto bei CreatorOffice, melde dich an; sonst lege es hier an.</p>
      <div className="row">
        <a className="btn primary" href={anmelden}>Anmelden</a>
        {!neu && <button type="button" onClick={() => setNeu(true)}>Neues Konto anlegen</button>}
      </div>
      {neu && (
        <form className="stack" onSubmit={(e) => void anlegen(e)}>
          <label>E-Mail-Adresse (wie in der Einladung)
            <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>Passwort
            <input type="password" autoComplete="new-password" minLength={api.mode === "demo" ? undefined : MIN_PASSWORD_LENGTH} value={passwort} onChange={(e) => setPasswort(e.target.value)} />
            <small>{passwordHint("de")}</small>
          </label>
          {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={runde} />}
          <div className="row"><button type="submit" className="primary" disabled={busy} aria-busy={busy}>{busy ? "Wird angelegt …" : "Konto anlegen"}</button></div>
          <small className="muted">Danach kommt ein Code per Mail; mit ihm bestätigst du die Adresse und nimmst die Einladung an.</small>
        </form>
      )}
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
    </div>
  );
}
